import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, RouterLink, RouterLinkActive } from '@angular/router';
import { Observable, catchError, combineLatest, distinctUntilChanged, map, of, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { PortalAuthService } from './core/auth/portal-auth.service';
import { isProfileMissingError } from './core/auth/auth-errors';
import { PortalUser } from './core/domain/portal-user.model';
import { UserApiService } from './core/services/user-api.service';
import { CurrentTenantService } from './core/services/current-tenant.service';
import { NotificationApiService } from './core/services/notification-api.service';
import { LanguageService } from './core/core-services/language.service';
import { ThemeService } from './core/core-services/theme.service';
import { UiPreferencesService } from './core/core-services/ui-preferences.service';
import { AccountSettingsService } from './core/services/account-settings.service';
import { MenuConfigService } from './core/services/menu-config.service';
import { environment } from '../environments/environment';
import { AppBootstrapService } from './core/services/app-bootstrap.service';
import { ShellOverlayService } from './core/services/shell-overlay.service';
import { PageTransitionService } from './core/services/page-transition.service';
import { IdleTimeoutService } from './core/services/idle-timeout.service';
import { PageTransitionOverlayComponent } from './core/components/page-transition-overlay/page-transition-overlay.component';
import { GlobalAlertComponent } from './core/components/global-alert/global-alert.component';
import { ConnectionBannerComponent } from './core/components/connection-banner/connection-banner.component';
import { CookieConsentComponent } from './core/components/cookie-consent/cookie-consent.component';
import { IonicModule } from '@ionic/angular';
import { NotificationsCenterComponent } from './core/components/notifications-center/notifications-center.component';
import { UserMenuComponent } from './core/components/user-menu/user-menu.component';
import { SideMenuComponent } from './core/components/side-menu/side-menu.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipDirective } from './core/directives/tooltip.directive';
import { avatarUrl } from './core/utils/avatar.util';

/**
 * Application shell. Reduced to the ion-app layout plus the toolbar/menu/outlet.
 * Notifications, the profile menu, the side navigation and the cross-cutting
 * bootstrap orchestration each live in their own component/service now.
 */
@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PageTransitionOverlayComponent,
        GlobalAlertComponent,
        ConnectionBannerComponent,
        CookieConsentComponent,
        IonicModule,
        RouterLink,
        RouterLinkActive,
        NotificationsCenterComponent,
        UserMenuComponent,
        SideMenuComponent,
        AsyncPipe,
        TranslatePipe,
        TooltipDirective,
    ],
})
export class AppComponent implements OnInit {
  readonly avatarUrl = avatarUrl;

  /** Public product wiki (Wiki-SPA), a separate site; linked from the public-nav row. */
  readonly wikiUrl = environment.wikiUrl;
  readonly isAuthenticated$ = this.portalAuth.isAuthenticated$;
  readonly user$: Observable<PortalUser | null>;
  readonly unreadCount$ = this.notificationApi.unreadCount$;
  /** Current tenant's display name, shown in the toolbar instead of the page name
   *  (the page-header already repeats that below) — tells the user which business
   *  they're currently in. Falls back to the product name while no tenant context is
   *  active (signed out, platform admin not "acting as" a tenant, onboarding, ...). */
  readonly tenantName$ = this.currentTenant.name$;
  /** True while a route change is in flight. Drives the header progress bar so
   *  lazy-chunk loads (e.g. the devices module, preload: false) don't feel frozen. */
  navigating$!: Observable<boolean>;
  /** True on MenuConfigService.publicWhenLoggedOutPages while signed out — those pages
   *  render their own app-portal-navbar instead, so the app shell's own header/side-menu
   *  is suppressed rather than the two stacking (see docs decision 2026-07-07). */
  hideAppShell$!: Observable<boolean>;
  /** True while the shell header is showing for one of those same pages and a user is
   *  signed in. Adds the fixed public nav links (products/pricing/status/developer/
   *  contact) as a second toolbar row on the shell header, so those pages keep the same
   *  links reachable from the header whether or not a user is logged in — instead of each
   *  page rebuilding its own copy of that row for the authenticated case. */
  showPublicNav$!: Observable<boolean>;

  /** True while the signed-in user has no profile yet (registration pending): the tenant gate must not claim "no company". */
  private profileMissing = false;

  constructor(
    private readonly portalAuth: PortalAuthService,
    private readonly userApi: UserApiService,
    private readonly currentTenant: CurrentTenantService,
    private readonly notificationApi: NotificationApiService,
    private readonly router: Router,
    private readonly language: LanguageService,
    private readonly menuConfig: MenuConfigService,
    private readonly bootstrap: AppBootstrapService,
    readonly overlay: ShellOverlayService,
    private readonly theme: ThemeService,
    private readonly uiPrefs: UiPreferencesService,
    private readonly pageTransition: PageTransitionService,
    private readonly accountSettings: AccountSettingsService,
    private readonly idleTimeout: IdleTimeoutService,
  ) {
    this.theme.init();
    this.uiPrefs.init();
    this.language.initLanguage();
    this.accountSettings.watch();

    // Shared user stream: resolves the authenticated profile once and pushes
    // tenant/role/platform-admin into the auth context. Consumed by the toolbar
    // avatar (UserMenuComponent) via the [user$] input.
    // Loaded again whenever profileRefresh$ fires: a new member's first load happens before registration
    // has created the profile, onboarding asks for the second one when it sees the profile appear.
    this.user$ = combineLatest([
      this.portalAuth.isAuthenticated$,
      this.portalAuth.profileRefresh$.pipe(startWith(undefined)),
    ]).pipe(
      switchMap(([auth]): Observable<PortalUser | null> => auth
        ? this.userApi.getMe().pipe(
          tap(u => {
            this.profileMissing = false;
            this.portalAuth.setAuthContext(u.tenantId ?? null, u.role ?? '', u.isPlatformAdmin ?? false, u.defaultOrgId);
            this.accountSettings.sync(u);
          }),
          // getMe() failing must not propagate: an uncaught error here would (a) never
          // call setAuthContext, leaving hasTenant$/userRole$ stuck at their initial
          // null seed so tenantGuard/roleGuard (no timeout) hang forever, and (b) error
          // out this shared, shareReplay'd user$ - permanently killing every other
          // long-lived subscriber (e.g. UserMenuComponent). Catch it inside the inner
          // (per-emission) pipe, not outside switchMap, so the outer subscription to
          // isAuthenticated$ stays alive for future auth-state changes (e.g. a later
          // logout/login in the same tab) instead of the whole chain completing once.
          catchError(err => {
            // Signed in, but no profile yet: a new member who still has to finish (or is waiting for) the
            // registration. That is onboarding, not "no company" - say so before the context below flips
            // the tenant gate, which would otherwise send them to /no-tenant from wherever they opened the portal.
            if (err instanceof HttpErrorResponse && isProfileMissingError(err)) {
              this.profileMissing = true;
              this.portalAuth.setAuthContextError();
              void this.router.navigate(['/onboarding']);
              return of<PortalUser | null>(null);
            }
            console.error('[AppComponent] Failed to load the authenticated user profile (getMe); ' +
              'falling back to a definite auth context so guards do not hang.', err);
            this.portalAuth.setAuthContextError();
            return of<PortalUser | null>(null);
          }))
        : of(null)),
      startWith<PortalUser | null>(null),
      shareReplay(1),
    );
  }

  ngOnInit(): void {
    this.forwardOrganizationInvitation();

    this.navigating$ = this.router.events.pipe(
      filter(e => e instanceof NavigationStart || e instanceof NavigationEnd
        || e instanceof NavigationCancel || e instanceof NavigationError),
      map(e => e instanceof NavigationStart),
      distinctUntilChanged(),
      startWith(false),
    );

    const url$ = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    );

    this.hideAppShell$ = combineLatest([this.isAuthenticated$, url$]).pipe(
      map(([auth, url]) => {
        const path = url.split(/[?#]/)[0];
        return this.menuConfig.publicWhenLoggedOutPages.includes(path) && !auth;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.showPublicNav$ = combineLatest([this.isAuthenticated$, url$]).pipe(
      map(([auth, url]) => {
        const path = url.split(/[?#]/)[0];
        return !!auth && this.menuConfig.publicWhenLoggedOutPages.includes(path);
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.bootstrap.start();

    // End the session after a period without activity. Only runs while signed in; the notice after an
    // idle logout is shown on the signed-out page that follows the redirect.
    this.idleTimeout.showExpiredNoticeIfAny();
    this.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(auth => auth ? this.idleTimeout.start() : this.idleTimeout.stop());

    // Reactive tenant gate: 'home'/'settings' intentionally carry no tenantGuard (home also
    // serves as the public marketing page for logged-out visitors, settings shows
    // tenant-independent personal info), and this component's own user$/getMe() failure
    // (unlike callback.page.ts) never navigates anywhere on error. Without this, a
    // non-admin user whose tenant context resolves to "none" (reopened tab, hard refresh,
    // revoked membership) stays wherever they were with the full shell rendered normally
    // instead of being sent to /no-tenant — per-route tenantGuard only runs on navigation,
    // not on a later state change while already sitting on an un-gated route.
    combineLatest([this.portalAuth.hasTenant$, this.portalAuth.isPlatformAdmin$, url$]).pipe(
      filter(([hasTenant, isAdmin]) => hasTenant === false && isAdmin === false && !this.profileMissing),
      map(([, , url]) => url.split(/[?#]/)[0]),
      filter(path => !['/no-tenant', '/onboarding', '/callback', '/invite'].includes(path)),
    ).subscribe(() => this.router.navigate(['/no-tenant']));

    // Releases the page-transition overlay once a route it was guarding
    // (pageTransitionGuard) finishes — whether it activated, was cancelled,
    // or errored. No-ops when no such transition is pending.
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
    ).subscribe(() => this.pageTransition.endRouteTransitionIfPending(this.router.url));
  }

  /**
   * Sends an Auth0 Organization invitation link on to /invite, wherever it lands.
   *
   * Auth0 builds the invite mail's link from the application's "Application Login URI" and
   * appends ?invitation=…&organization=…&organization_name=… — so which route receives those
   * parameters is an Auth0 dashboard setting, not ours (it pointed at /home until now, where
   * nothing read them and the invitee had to hunt for the login button; the plain login that
   * followed carried no invitation, so the membership was never established). InvitePage is
   * the only place that redeems them, so anything else carrying an invitation is forwarded
   * there with the query string intact.
   */
  private forwardOrganizationInvitation(): void {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('invitation') || !params.has('organization')) return;
    if (window.location.pathname === '/invite') return;

    this.router.navigate(['/invite'], { queryParams: Object.fromEntries(params.entries()) });
  }
}
